import React, {useState, useEffect, useCallback} from 'react';
import AddressSearch from "../../../components/Form/Search/Address/AddressSearch";
import {deleteAddress, persistAddress, readAddress} from "../../../services/localStorage.service";
import {getPlans, Plan, Router, Device} from "../../../api/address.api";
import PlanFindError from "../../../components/Form/Search/Plans/PlanFindError";
import PlansNotFound from "../../../components/Form/Search/Plans/PlansNotFound";
import LoaderFullScreen from "../../../components/Page/LoaderFullScreen";
import SectionMessage from "../../../components/Page/SectionMessage";
import SectionWrapper from "../../../components/Page/SectionWrapper";
import BroadbandPlan from "../../../components/Plan/BroadbandPlan";
import BroadbandRouter from "../../../components/Plan/BroadbandRouter";
import BroadbandRouterBYO from "../../../components/Plan/BroadbandRouterBYO";
import YourCart from "../../../components/Cart/YourCart";
import {Cart, CartProduct} from "../../../@types/cart";
import {AddressSuggestion} from "../../../@types/address";
import YourBottomCart from "../../../components/Cart/YourBottomCart";
import useSignupContext from "../../../context/SignupContext";
import {useNavigate} from "react-router-dom";
import ButtonPrimary from "../../../components/Form/Button/ButtoPrimary";

const SearchAddress: React.FC = () => {

  const navigate = useNavigate();
  const { clearContext, persistProducts, persistCart, persistPlans, productItems, cachedPlans, cartItems  } = useSignupContext();

  const [selectedAddress, setSelectedAddress] = useState<AddressSuggestion | null>(null);
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [products, setProducts] = useState<CartProduct[] | null>(null);
  const [cart, setCart] = useState<Cart | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedRouter, setSelectedRouter] = useState<Device | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [isBYO, setIsBYO] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  const getPlansForTui = useCallback(async ()=> {
    if (!selectedAddress) {
      setPlans(null);
      resetSelectedProducts();
      return;
    }

    setCart((prevState) => {
      return ({
        ...prevState,
        address: selectedAddress
      });
    });

    setIsLoading(true);
    setPlanError(null);

    try {
      let tempPlans: Plan[] | null = null;
      // Check for cached plans
      if (cachedPlans && cachedPlans.uuid === selectedAddress.uuid) {
        tempPlans = cachedPlans.plans;
      } else {
        const results = await getPlans(selectedAddress.uuid, selectedAddress.source);
        if (results.success) {
          tempPlans = results.plans;
        }
      }

      if (tempPlans != null) {
        setPlans(tempPlans);

        // Check for saved products in the context
        productItems?.forEach((productItem) => {
          if (productItem.type === 'PLAN') {
            // Check for matching plan
            const savedPlan = tempPlans?.find((plan) => plan.plan_id === productItem.productId);
            if (savedPlan) {
              setSelectedPlan(savedPlan);

              // Check for selected router
              const savedRouter = productItems.find((product) => product.type === 'ROUTER');
              if (savedRouter) {
                if (savedRouter.productId === 'ROUTER_BYO' && savedPlan.allow_byo) {
                  selectRouterBYO();
                } else {
                  const router = savedPlan.devices.find((device) => device.device_id === savedRouter.productId);
                  if (router) {
                    selectRouter(router);
                  }
                }
              }
            }
          }
        });

        // Auto-select recommended plan if no plan is already selected
        if (!selectedPlan) {
          const recommendedPlan = tempPlans.find((plan) => plan.recommended === true);
          if (recommendedPlan) {
            setSelectedPlan(recommendedPlan);
          }
        }

      } else {
        setPlanError('Unable to find plans for your selected address. Please try again.');
        setPlans(null);
      }
    } catch (e) {
      setPlanError('Unable to find plans for your selected address. Please try again.');
      setPlans(null);
      resetSelectedProducts();
    } finally {
      setIsLoading(false);
    }
  }, [selectedAddress]);

  const resetSelectedProducts = () => {
    setSelectedPlan(null);
    setSelectedRouter(null);
    setProducts(null);
  };

  const onAddressSelected = (selectedAddress: AddressSuggestion) => {
    setPlanError(null);
    setSelectedAddress(selectedAddress);
    persistAddress(selectedAddress);
  };

  const handlePlanSelection = (plan: Plan) => {
    // Check if the selected plan is different from the current plan
    if (selectedPlan && selectedPlan.plan_id !== plan.plan_id) {
      // Reset promo code when plan changes
      setCart((prevCart) => {
        if (prevCart) {
          const updatedCart = {
            ...prevCart,
            products: (prevCart.products || []).filter(p => p.type !== 'PROMO')
          };
          persistCart(updatedCart);
          return updatedCart;
        }
        return prevCart;
      });
    }
    setSelectedPlan(plan);
  };

  const resetSelectedAddress = () => {
    setSelectedAddress(null);
    setPlans(null);
    setProducts(null);
    setCart(null);
    setSelectedPlan(null);
    setSelectedRouter(null);
    setPlanError(null);

    // Delete address from local storage
    deleteAddress();

    // Clear context data
    clearContext();
  };

  const selectRouter = (device: Device) => {
    setIsBYO(false);
    setSelectedRouter(device);
    addNewProduct({
      productId: device.device_id,
      productName: device.name,
      cost: parseFloat(device.cost_device_inc_gst) * 100,
      costShipping: parseFloat(device.cost_shipping_inc_gst) * 100,
      type: "ROUTER",
      isMonthly: device.cost_type === 'monthly',
      months: device.cost_term
    });

    // If router has shipping fee, add one
    if (parseFloat(device.cost_shipping_inc_gst) > 0) {
      selectShipping(device);
    }
  };

  const selectShipping = (device: Device) => {
    addNewProduct({
      productId: 'ROUTER_SHIPPING',
      productName: 'Router delivery fee',
      cost: parseFloat(device.cost_shipping_inc_gst) * 100,
      costShipping: 0,
      type: "SHIPPING",
      isMonthly: false,
      months: 0
    });
  };

  const selectRouterBYO = () => {
    setIsBYO(true);
    setSelectedRouter(null);
    addNewProduct({
      productId: 'ROUTER_BYO',
      productName: 'BYO Router',
      cost: 0,
      costShipping: 0,
      type: "ROUTER",
      isMonthly: false,
      months: 0
    });
  };

  const addNewProduct = (product: CartProduct) => {
    setProducts((prevState) => {
      if (prevState !== null) {
        let updatedItems;
        // Check to see if the product is already in the collection
        if (product.type === 'ROUTER' || product.type === 'PLAN') {
          // Only one router product and one plan product
          updatedItems = prevState.filter(item => item.type !== product.type);
          updatedItems = [...updatedItems, product];

          // return updatedItems;
        } else {
          const item = prevState.find((item) => item.productId === product.productId);
          if (!item) return ([...prevState, product]);

          updatedItems = ([...prevState]);
        }

        // Check to see if there's a BYO router
        const isBYO = updatedItems.find((item) => item.productId === 'ROUTER_BYO');
        if (isBYO) {
          updatedItems = updatedItems.filter((item) => item.productId !== 'ROUTER_SHIPPING');
        }

        return updatedItems;
      } else {
        return ([product]);
      }
    });
  };

  useEffect(() => {
    if (selectedAddress != null) {
      getPlansForTui();
    }

  }, [getPlansForTui, selectedAddress]);

  useEffect(() => {
    setCart((prevState) => {
      if (prevState != null) {
        return ({
          ...prevState,
          products: products
        });
      } else {
        return ({
          address: selectedAddress,
          products: products
        });
      }
    });
  }, [products, selectedAddress]);

  useEffect(() => {
    if (products != null) {
      persistProducts(products);
    }
  }, [persistProducts, products]);

  useEffect(() => {
    if (cart != null) {
      persistCart(cart);
    }
  }, [cart, persistCart]);

  useEffect(() => {
    if (selectedPlan != null) {
      addNewProduct({
        productId: selectedPlan.plan_id,
        productName: selectedPlan.name,
        type: 'PLAN',
        cost: parseFloat(selectedPlan.unlimited_cost_inc_gst ?? '0') * 100,
        costShipping: 0,
        isMonthly: true,
        months: 0
      })
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (selectedAddress && plans) {
      persistPlans({
        uuid: selectedAddress?.uuid,
        plans: plans
      });
    }
  }, [persistPlans, plans, selectedAddress]);

  useEffect(() => {
    setSelectedAddress(readAddress);
  }, []);

  return (
      <div className='grow'>
        <div className="p-4 md:p-8 w-full">
          {isLoading && <LoaderFullScreen message="Loading plans..."/>}
          <section className='py-8 lg:py-4'>
            <SectionWrapper>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-fred-purple-500">Sign-Up for FREDNET Residential Broadband</h1>
              {!selectedAddress ? (
                <div className='mt-2 sm:mt-4'>
                  <p className='text-sm sm:text-lg md:text-xl font-light mb-2'>Enter your address below to see what's available</p>
                  <AddressSearch name='address-search' value='h' onAddressSelected={onAddressSelected}/>
                </div>
              ) : null}
              {selectedAddress && (
                  <div className='mt-4 p-4 bg-fred-blue-50 border-2 border-fred-blue-200 rounded-lg shadow-md'>
                    <p className='text-sm sm:text-lg md:text-xl font-medium text-gray-800'>Following plans are available at the address <span
                        className="font-bold text-fred-purple-600 bg-white px-2 py-1 rounded">{selectedAddress.fullAddress}</span> - <span
                        className="font-bold underline cursor-pointer text-fred-blue-600 hover:text-fred-blue-800 transition-colors"
                        onClick={() => resetSelectedAddress()}>CHANGE</span></p>
                  </div>
              )}
            </SectionWrapper>
          </section>
          {planError && (
              <SectionMessage>
                <PlanFindError error={planError}/>
              </SectionMessage>
          )}
          {plans && plans.length < 1 && (
              <SectionMessage>
                <PlansNotFound/>
              </SectionMessage>
          )}
          {plans && plans.length > 0 && (
              <section>
                <div className='grid lg:grid-cols-3 items-start mx-auto max-w-screen-2xl gap-8'>
                  <div className='lg:col-span-2 space-y-8'>
                    <div className="gap-4 grid grid-cols-2">
                      <div className=' col-span-full mb-2'>
                        <h3 className="text-2xl font-medium text-fred-purple-500">Select Your Plan</h3>
                      </div>
                      {plans.map((plan, index) => {
                        return (
                            <BroadbandPlan key={index} plan={plan} selectedPlan={selectedPlan}
                                           onSelect={handlePlanSelection}/>
                        )
                      })}
                    </div>
                    {selectedPlan && (
                        <div className="gap-4 items-start grid grid-cols-2">
                          <div className=' col-span-full mb-2'>
                            <h3 className="text-2xl font-medium text-fred-purple-500">Do You Need a Router?</h3>
                          </div>
                          {selectedPlan.devices.map((device, index) => {
                            return (
                                <BroadbandRouter key={index} router={device} selectedRouter={selectedRouter}
                                                 onSelect={(selectedRouter) => selectRouter(selectedRouter)}/>
                            )
                          })}
                          {selectedPlan.allow_byo && (
                              <BroadbandRouterBYO selected={isBYO} onSelect={() => selectRouterBYO()}/>
                          )}
                        </div>
                    )}
                    <ButtonPrimary text="Continue" disabled={!isValid} onClick={() => navigate('/contact-details')} />
                  </div>
                  <YourCart cart={cart} buttonText='Continue' hideButton onChangeAddress={() => resetSelectedAddress()} onValidated={(valid) => setIsValid(valid)}/>
                </div>
              </section>
          )}
        </div>
        <div className="sticky bottom-0 z-50 w-full flex lg:hidden">
          {plans && plans.length > 0 && (
            <YourBottomCart cart={cart} buttonText='Continue' onSignup={() => navigate('/contact-details')} onChangeAddress={() => resetSelectedAddress()} onValidated={(valid) => setIsValid(valid)}/>
          )}
        </div>
      </div>
  );
};

export default SearchAddress;
